<template>
    <Popin v-if="isProjectListDesktopScreen" :width="popinSize" placement="bottom-start" @open="handleOpen" @close="handleClose">
        <template #button="{isOpened}">
            <div ref="filterRef" :class="{isOpened, isSelected}" class="search-filter / flex">
                <div class="search-filter_button / flex">
                    <span class="search-filter_button_title">{{ props.title }}</span>
                    <div v-if="selectedCount > 0" class="search-filter_button_selected">
                        <span class="search-filter_button_selected_count">{{ selectedCount }}</span>
                    </div>
                    <VJoyIcon name="chevron-down" size="xxsmall" color="neutral" />
                </div>
            </div>
        </template>
        <template #content>
            <slot />
        </template>
    </Popin>
    <slot v-else />
</template>

<script setup lang="ts">
    import {ref, useTemplateRef, computed} from 'vue';
    import {useScreenSizeStore} from '@common/stores';
    import {Popin} from '@common/ui';
    import {storeToRefs} from 'pinia';
    import {VJoyIcon} from '@maltjoy/core-vue';

    const props = withDefaults(
        defineProps<{
            title: string;
            selectedCount?: number;
            size?: 'medium' | 'large';
        }>(),
        {selectedCount: 0, size: 'large'},
    );

    const emit = defineEmits<{
        open: [];
        close: [];
    }>();

    const popinSize = computed(() => (props.size === 'medium' ? 150 : 450));

    const screenSizeStore = useScreenSizeStore();
    const {isProjectListDesktopScreen} = storeToRefs(screenSizeStore);

    const filterRef = useTemplateRef('filterRef');
    const storedSibling = ref<Element | null>(null);

    const isSelected = computed(() => props.selectedCount > 0);

    function handleOpen() {
        emit('open');
    }

    function handleClose() {
        if (storedSibling.value) {
            storedSibling.value.classList.remove('sibling-opened');
            storedSibling.value = null;
        }
        emit('close');
    }
</script>

<style lang="scss" scoped>
    .search-filter {
        --radius-widths: 0;

        flex-flow: row nowrap;
        align-items: center;
        border-color: var(--joy-color-neutral-30);
        border-style: solid;
        height: 40px;
        padding: var(--joy-core-spacing-4);
        border-width: 1px 1px 1px 0;
        border-radius: var(--radius-widths);
        cursor: pointer;
        user-select: none;

        $r: &;

        flex: 0 0 auto;

        &_button {
            flex-flow: row nowrap;
            align-items: center;
            font-size: var(--joy-font-size-primary-300);

            &_title {
                color: var(--joy-color-neutral-60);
                text-wrap: nowrap;
                flex: 0 0 auto;
            }

            &_selected {
                margin: 0 var(--joy-core-spacing-1);

                &_count {
                    background-color: var(--joy-color-secondary-30);
                    color: var(--joy-color-neutral-0);
                    border-radius: var(--joy-core-radius-4);
                    padding: 1px var(--joy-core-spacing-2);
                    display: inline-flex;
                    align-items: center;
                    padding-bottom: 2px;
                }
            }
        }

        &:first-of-type {
            --radius-widths: var(--joy-core-radius-3) 0 0 var(--joy-core-radius-3);

            border-width: 1px;
        }

        &:last-of-type {
            --radius-widths: 0 var(--joy-core-radius-3) var(--joy-core-radius-3) 0;

            border-width: 1px 1px 1px 0;
        }

        &_button {
            .joy-icon {
                margin-left: var(--joy-core-spacing-1);
            }
        }

        &.isSelected,
        &.isOpened {
            background-color: var(--joy-color-secondary-10);

            &::after {
                content: '';
                position: absolute;
                inset: -1px;
                width: calc(100% + 2px);
                height: calc(100% + 2px);
                border-radius: var(--radius-widths);
                border: 1px solid var(--joy-color-secondary-30);
                z-index: 10;
            }
        }

        &:hover:not(.isOpened, .isSelected) {
            background-color: var(--joy-color-neutral-10);
        }
    }
</style>
